import React, { useState, useEffect, useRef, KeyboardEvent, forwardRef } from 'react';
import { supabase } from '../../../lib/supabase';
import Pill from '../display/Pill';
import { captureAppException } from '../../../utils/errorCaptureUtils';
import clsx from 'clsx';

interface Tag {
  id: string;
  name: string;
  type: string;
  logo_url?: string | null;
}

// Extend from the same props as Input component
interface TagInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  selectedTags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
  searchTags?: (searchTerm: string) => Promise<Tag[]>;
  renderTagContent?: (tag: Tag) => React.ReactNode;
  tagPillClassName?: string;
  className?: string;
  error?: boolean;
  showCount?: boolean;
  maxLength?: number;
  label?: string;
  helperText?: string;
  isRequired?: boolean;
  showSuggestionsDropdown?: boolean;
}

export const TagInput = forwardRef<HTMLInputElement, TagInputProps>(({
  selectedTags, 
  onTagsChange,
  searchTags: customSearchTags,
  renderTagContent,
  tagPillClassName = "bg-primary-100 text-primary-base",
  className,
  error,
  showCount,
  maxLength,
  type = 'text', // default to text, but could be 'email', etc.
  label,
  helperText,
  placeholder = "Type and press enter to add...",
  isRequired = false,
  disabled = false,
  showSuggestionsDropdown = true,
  autoFocus = false,
  ...props
}, ref) => {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<Tag[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  
  // Merge the forwarded ref with our internal ref
  const handleRef = (element: HTMLInputElement) => {
    // Update our internal ref
    inputRef.current = element;
    
    // Forward the ref
    if (typeof ref === 'function') {
      ref(element);
    } else if (ref) {
      ref.current = element;
    }
  };

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        !inputContainerRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchTags = async (searchTerm: string) => {
    if (!searchTerm || !showSuggestionsDropdown) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      let results: Tag[] = [];
      
      if (customSearchTags) {
        results = await customSearchTags(searchTerm);
      } else {
        // Original search implementation for other tag types
        const [
          { data: industries },
          { data: jobFunctions },
          { data: platforms },
          { data: programmingLanguages },
          { data: sectors },
          { data: subsectors },
          { data: tools },
        ] = await Promise.all([
          supabase
            .from('industries')
            .select('id, name')
            .ilike('name', `%${searchTerm}%`)
            .limit(5),
          supabase
            .from('job_functions')
            .select('id, name')
            .ilike('name', `%${searchTerm}%`)
            .limit(5),
          supabase
            .from('platforms')
            .select('id, name, logo_url')
            .ilike('name', `%${searchTerm}%`)
            .limit(5),
          supabase
            .from('programming_languages')
            .select('id, name')
            .ilike('name', `%${searchTerm}%`)
            .limit(5),
          supabase
            .from('sectors')
            .select('id, name')
            .ilike('name', `%${searchTerm}%`)
            .limit(5),
          supabase
            .from('subsectors')
            .select('id, name')
            .ilike('name', `%${searchTerm}%`)
            .limit(5),
          supabase
            .from('tools')
            .select('id, name, logo_url')
            .ilike('name', `%${searchTerm}%`)
            .limit(5),
        ]);

        results = [
          ...(industries || []).map((item) => ({ ...item, type: 'industry' })),
          ...(jobFunctions || []).map((item) => ({ ...item, type: 'job_function' })),
          ...(platforms || []).map((item) => ({ ...item, type: 'platform', logo_url: item.logo_url })),
          ...(programmingLanguages || []).map((item) => ({ ...item, type: 'programming_language' })),
          ...(sectors || []).map((item) => ({ ...item, type: 'sector' })),
          ...(subsectors || []).map((item) => ({ ...item, type: 'subsector' })),
          ...(tools || []).map((item) => ({ ...item, type: 'tool', logo_url: item.logo_url })),
        ];
      }

      // Filter out already selected tags
      const filteredSuggestions = results.filter(
        (suggestion) => !selectedTags.some((tag) => tag.id === suggestion.id)
      );

      setSuggestions(filteredSuggestions);
      if (showSuggestionsDropdown) {
        setShowSuggestions(true);
      }
    } catch (error) {
      captureAppException(error, {
        tags: {
          context: 'tag_input',
          action: 'search_tags',
          search_term: searchTerm,
          custom_search: customSearchTags ? 'true' : 'false'
        }
      });
      console.error('Error searching tags:', error);
    } finally {
      setLoading(false);
    }
  };

  // Email validation (when type is email)
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(email);
  };

  const validateInput = (inputValue: string): boolean => {
    if (!inputValue.trim()) return false;
    
    // If this is an email input, validate the email format
    if (type === 'email') {
      const isValid = validateEmail(inputValue);
      if (!isValid) {
        setInternalError('Please enter a valid email address');
      }
      return isValid;
    }
    
    // Other validation types could be added here
    return true;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInput(value);
    setInternalError(null);
    searchTags(value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && input.trim()) {
      e.preventDefault();
      
      if (!validateInput(input)) {
        return;
      }
      
      // If suggestions are shown and there are matching suggestions, use the first one
      if (showSuggestions && suggestions.length > 0 && showSuggestionsDropdown) {
        addTag(suggestions[0]);
      } else {
        // Create a tag from the input directly
        addTag({
          id: input.trim(),
          name: input.trim(),
          type: type === 'email' ? 'email' : 'custom'
        });
      }
    } else if (e.key === 'Backspace' && !input && selectedTags.length > 0) {
      // Remove the last tag when pressing backspace in an empty input
      const lastTag = selectedTags[selectedTags.length - 1];
      removeTag(lastTag);
    }
  };

  const addTag = (tag: Tag) => {
    // Check if tag already exists to avoid duplicates
    if (!selectedTags.some(t => t.id === tag.id)) {
      onTagsChange([...selectedTags, tag]);
    }
    setInput('');
    setInternalError(null);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const removeTag = (tagToRemove: Tag) => {
    // Create a new array without the removed tag
    const newTags = selectedTags.filter((tag) => tag.id !== tagToRemove.id);

    // Update parent component
    onTagsChange(newTags);
  };

  return (
    <div className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {isRequired && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Input Container with Tags */}
      <div 
        ref={inputContainerRef}
        className={clsx(
          'flex flex-wrap items-center gap-2 w-full min-h-[3rem] px-3 py-2 border rounded-lg focus-within:ring-2 transition-colors',
          (error || internalError) 
            ? 'border-red-300 focus-within:border-red-500 focus-within:ring-red-500' 
            : 'border-gray-300 focus-within:border-primary-base focus-within:ring-primary-base',
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white',
          className
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {/* Tags Display */}
        {selectedTags.map((tag) => (
          <div
            key={tag.id}
            className="relative group flex items-center"
          >
            <Pill
              text={renderTagContent ? renderTagContent(tag) : tag.name}
              className={tagPillClassName}
            />
            {!disabled && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeTag(tag);
                }}
                className="absolute -right-2 -top-2 bg-red-500 text-white rounded-full h-5 w-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label={`Remove ${tag.name}`}
              >
                &times;
              </button>
            )}
          </div>
        ))}

        {/* Input Field (simplified, leveraging the Input component's styles) */}
        <input
          ref={handleRef}
          type={type}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => input && showSuggestionsDropdown && setShowSuggestions(true)}
          placeholder={selectedTags.length > 0 ? '' : placeholder}
          disabled={disabled}
          className="flex-grow min-w-[80px] outline-none bg-transparent py-1"
          {...props}
        />
      </div>

      {/* Helper Text or Error Message */}
      {(helperText || internalError) && (
        <p className={clsx('mt-2 text-sm', (error || internalError) ? 'text-red-500' : 'text-gray-500')}>
          {internalError || helperText}
        </p>
      )}

      {/* Suggestions Dropdown */}
      {showSuggestionsDropdown && showSuggestions && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto"
        >
          {loading ? (
            <div className="p-4 text-center text-gray-500">Loading...</div>
          ) : suggestions.length > 0 ? (
            <div className="py-2">
              {suggestions.map((suggestion) => (
                <button
                  key={`${suggestion.type}-${suggestion.id}`}
                  onClick={() => addTag(suggestion)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                >
                  <span className="flex items-center">
                    {suggestion.logo_url && (
                      <img 
                        src={suggestion.logo_url}
                        alt={suggestion.name}
                        className="w-4 h-4 object-contain mr-1.5"
                      />
                    )}
                    {suggestion.name}
                  </span>
                  <span className="text-xs text-gray-500">
                    {suggestion.type.replace(/_/g, ' ')}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-gray-500">No suggestions found</div>
          )}
        </div>
      )}
    </div>
  );
});

TagInput.displayName = 'TagInput';